import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlassIcon,
  ClockIcon,
  AcademicCapIcon,
  CheckBadgeIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import { getAvailableExams } from "../../services/Attempts/AttemptService";
import { getCategories } from "../../services/Exams/ExamsService";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import { ExamStatusBadge, examStatus } from "../Exams/ExamStatus";
import { StudentTitle } from "./parts";

export default function AvailableExams() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const controllerRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    getCategories({ signal: controller.signal })
      .then((data) => {
        const results = Array.isArray(data) ? data : data?.results || [];
        setCategories(results);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    let isCurrent = true;

    setLoading(true);
    getAvailableExams({
      params: {
        search: debouncedSearch || undefined,
        category: categoryFilter || undefined,
      },
      signal: controller.signal,
    })
      .then((data) => {
        if (!isCurrent) return;
        const results = Array.isArray(data)
          ? data
          : data?.results || data?.data || [];
        setExams(results);
      })
      .catch(() => {})
      .finally(() => {
        if (!isCurrent) return;
        setLoading(false);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [debouncedSearch, categoryFilter]);

  return (
    <div className="w-full motion-safe:animate-fade-in">
      <StudentTitle rowClassName="mb-6">{t("exam_taking.available_exams")}</StudentTitle>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <div className="relative min-w-[200px] flex-1 sm:max-w-md">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("exam_taking.search_placeholder")}
            aria-label={t("exam_taking.search_placeholder")}
            className="h-11 w-full rounded-lg border-slate-300 pl-10 pr-4 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 sm:h-10"
          />
        </div>

        {/* With no categories the picker would offer "All categories" alone. */}
        {(categories.length > 0 || categoryFilter) && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label={t("exam_taking.category")}
            className="h-11 rounded-lg border-slate-300 pl-3 pr-9 text-sm text-slate-700 shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 sm:h-10 sm:w-auto"
          >
            <option value="">{t("exam_taking.all_categories")}</option>
            {categories.map((cat) => (
              <option key={cat.uuid} value={cat.uuid}>
                {cat.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <SimpleLoader className="h-8 w-8" />
      ) : exams.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
          <span className="mb-4 rounded-full bg-slate-100 p-3 text-slate-400">
            <AcademicCapIcon className="h-6 w-6" aria-hidden="true" />
          </span>
          <p className="text-sm font-medium text-slate-900">{t("exam_taking.no_exams_available")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4">
          {exams.map((exam) => (
            <div
              key={exam.uuid}
              role="link"
              tabIndex={0}
              className="group flex cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 motion-reduce:hover:translate-y-0"
              onClick={() => navigate(`/exam/${exam.uuid}/take`)}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(`/exam/${exam.uuid}/take`);
              }}
            >
              <div className="mb-4 flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-colors duration-200 group-hover:bg-blue-600 group-hover:text-white">
                  <AcademicCapIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <h3 className="mb-1 line-clamp-2 font-semibold text-slate-900 transition-colors duration-200 group-hover:text-blue-700">
                    {exam.name}
                  </h3>
                  {/* Students get the active exams only; exam managers see every status, and which ones students don't. */}
                  {examStatus(exam) && examStatus(exam) !== "active" && (
                    <span title={t("exam_bank.not_visible_to_students")} className="mb-1.5 inline-flex">
                      <ExamStatusBadge status={examStatus(exam)} />
                      <span className="sr-only">{t("exam_bank.not_visible_to_students")}</span>
                    </span>
                  )}
                  {exam.description && (
                    <p className="line-clamp-2 text-sm leading-6 text-slate-500">
                      {exam.description}
                    </p>
                  )}
                </div>
              </div>
              {/* Plain facts, the passing score named (a bare blue "70%" told students nothing). */}
              <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-4 text-sm text-slate-600">
                <span className="flex items-center gap-1.5">
                  <ClockIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  {t("exam_taking.minutes_value", { minutes: exam.duration_minutes })}
                </span>
                <span className="flex items-center gap-1.5">
                  <ListBulletIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  {t("exam_taking.tests_count", {
                    count: exam.tests_count ?? 0,
                  })}
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckBadgeIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  {t("exam_taking.passing_score")}: {exam.passing_score}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
